import { useEffect, useMemo, useState } from 'react';
import { ArrowRight, MoreVertical, Eye, EyeOff } from 'lucide-react';
import pluralize from 'pluralize';
import TagNameBadge from 'src/shared/components/entities/components/TagNameBadge';
import PrimitiveEntityValue from 'src/shared/components/entities/components/PrimitiveEntityValue';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItemWithIcon,
  DropdownMenuTrigger,
} from 'src/shared/components/ui/dropdown-menu';
import { Separator } from 'src/shared/components/ui/separator';
import { cn, uniqueArray } from 'src/shared/lib/utils';
import { ActualsMetaNer, ActualsUpdateType } from 'src/features/messages/types/message.types';
import { Tag } from 'src/shared/types/model.types';
import { getMissingsTags } from 'src/shared/lib/entities.utils';

type PrimitiveEntityListProps = {
  path?: string[];
  parentTag?: Tag;
  variant?: 'sm' | 'xs';
  actuals: ActualsMetaNer;
  tags: Tag[];
  entities: Record<string, string[] | null>;
  showEmptyTags?: boolean;
  smallLayout?: boolean;
  onEditSave?: (values: string[], path: string[], action: ActualsUpdateType) => void;
  onDuplicate?: (path: string[]) => void;
  onDelete?: (path: string[]) => void;
};

export default function PrimitiveEntityList({
  actuals,
  path = [],
  parentTag,
  tags = [],
  entities = {},
  variant = 'sm',
  showEmptyTags = true,
  smallLayout = false,
  onEditSave,
  onDuplicate,
  onDelete,
}: PrimitiveEntityListProps) {
  const [showMissingTags, setShowMissingTags] = useState(onEditSave && showEmptyTags ? true : false);
  const [highlightPath, setHighlightPath] = useState<string[]>([]);

  useEffect(() => {
    setShowMissingTags(!!onEditSave && showEmptyTags);
  }, [onEditSave, showEmptyTags]);

  const missingTags = useMemo(() => {
    return getMissingsTags(entities, tags);
  }, [tags, entities]);

  const filteredEntities = useMemo(
    () => uniqueArray(tags, 'internalId').filter((tag) => showMissingTags || entities[tag.internalId]?.length),
    [tags, showMissingTags, entities]
  );

  const hasHeading = missingTags.length > 0 || (parentTag?.children || []).length > 0;

  return (
    <div className="flex flex-col group/primitive-entity-list">
      {hasHeading && (!smallLayout || !filteredEntities?.length) && (
        <div className="flex items-center gap-1 opacity-50 group-hover/primitive-entity-list:opacity-100">
          {missingTags.length > 0 && (
            <button
              className="flex shrink-0 py-0.5 pl-1 text-xs items-center gap-1 text-typography-grey hover:text-black"
              onClick={() => setShowMissingTags((prev) => !prev)}
            >
              {showMissingTags ? <EyeOff size={14} /> : <Eye size={14} />}
              {showMissingTags ? 'Hide' : 'Show'} {missingTags.length} empty {pluralize('tag', missingTags.length)}
            </button>
          )}
          {(parentTag?.children || []).length > 0 && parentTag?.isGrouped && (
            <DropdownMenu>
              <DropdownMenuTrigger className="flex select-none shrink-0 px-1.5 py-0.5 text-xs ml-auto items-center gap-1 text-typography-grey hover:text-black rounded-sm hover:bg-neutral-100">
                <MoreVertical size={14} />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-32 p-1">
                <DropdownMenuItemWithIcon
                  icon="Clipboard"
                  onClick={() => navigator.clipboard.writeText(JSON.stringify(entities, null, 4))}
                >
                  Copy
                </DropdownMenuItemWithIcon>
                {onDuplicate && (
                  <DropdownMenuItemWithIcon icon="Copy" onClick={() => onDuplicate(path)}>
                    Duplicate
                  </DropdownMenuItemWithIcon>
                )}
                {onDelete && (
                  <DropdownMenuItemWithIcon icon="Trash2" destructive onClick={() => onDelete(path)}>
                    Delete
                  </DropdownMenuItemWithIcon>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      )}
      {filteredEntities
        .sort((a, b) => a.shortName.localeCompare(b.shortName))
        .map((tag, index) => {
          const tagPath = path.concat(tag.internalId).join('.');
          const isVerified = !!(
            actuals?.verified || actuals?.updates?.[tagPath]?.some((update) => update.action.includes('VERIFY'))
          );
          return (
            <div
              key={tag.internalId}
              className={cn(
                'min-h-7 group/primitive-entity-value flex w-full flex-col',
                !smallLayout && 'flex-row items-center'
              )}
            >
              <div className={cn('flex', smallLayout ? 'items-start' : 'items-center')}>
                <div className={cn('shrink-0 self-start', !smallLayout && 'flex items-center gap-1')}>
                  <TagNameBadge
                    isPrimitive
                    size={variant}
                    verified={isVerified}
                    className={cn(!isVerified && 'group-hover/primitive-entity-value:bg-blue-100')}
                    tagName={tag.shortName}
                  />
                  {!smallLayout && <ArrowRight size={16} className="text-typography-grey shrink-0" />}
                </div>
                {index === 0 && smallLayout && (parentTag?.children || []).length > 0 && parentTag?.isGrouped && (
                  <DropdownMenu>
                    <DropdownMenuTrigger className="flex select-none shrink-0 px-1.5 py-0.5 text-xs ml-auto items-center gap-1 text-typography-grey hover:text-black rounded-sm hover:bg-neutral-100">
                      <MoreVertical size={14} />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-fit">
                      {(parentTag?.children || []).length > 0 && parentTag?.isGrouped && (
                        <>
                          <DropdownMenuItemWithIcon icon="EyeOff" onClick={() => setShowMissingTags((prev) => !prev)}>
                            {`Hide empty fields (${missingTags.length})`}
                          </DropdownMenuItemWithIcon>
                          <Separator className="m-1" />
                        </>
                      )}
                      <DropdownMenuItemWithIcon
                        className="p-1"
                        icon="Clipboard"
                        onClick={() => navigator.clipboard.writeText(JSON.stringify(entities, null, 4))}
                      >
                        Copy Section (.json)
                      </DropdownMenuItemWithIcon>
                      {onDuplicate && (
                        <DropdownMenuItemWithIcon className="p-1" icon="Copy" onClick={() => onDuplicate(path)}>
                          Duplicate Section
                        </DropdownMenuItemWithIcon>
                      )}
                      <Separator className="my-1" />
                      {onDelete && (
                        <DropdownMenuItemWithIcon
                          className="p-1"
                          icon="Trash2"
                          destructive
                          onClick={() => onDelete(path)}
                        >
                          Delete
                        </DropdownMenuItemWithIcon>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
              <div className={cn('flex items-center', !smallLayout && 'flex-1 min-w-0 pl-0')}>
                {smallLayout && (
                  <ArrowRight size={16} className="text-typography-grey shrink-0 ml-0.5 self-start mt-2.5" />
                )}
                <div className={cn('flex-1 min-w-0 pl-1 py-1', smallLayout && 'pl-0')}>
                  <PrimitiveEntityValue
                    verified={isVerified}
                    path={tagPath.split('.')}
                    highlightPath={highlightPath}
                    values={entities[tag.internalId] || []}
                    onEditSave={onEditSave}
                    setHighlightPath={setHighlightPath}
                  />
                </div>
              </div>
            </div>
          );
        })}
    </div>
  );
}
